import { ChangeEvent, DragEvent, FormEvent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';

type MessageType = 'text' | 'image' | 'video' | 'audio' | 'document' | 'openai' | 'groq' | 'wait';

interface WhatsAppSession {
  name: string;
  displayName?: string;
  status: string;
  provider: string;
}

interface WAHAGroup {
  id?: string;
  subject?: string;
  name?: string;
  participants?: any[];
  [key: string]: any;
}

interface SequenceItem {
  type: MessageType;
  content: {
    text?: string;
    url?: string;
    caption?: string;
    fileName?: string;
    model?: string;
    system?: string;
    user?: string;
    waitTime?: number;
    mentionEveryone?: boolean;
  };
}

interface GroupCampaignForm {
  nome: string;
  messageType: 'sequence';
  messageContent: { sequence: SequenceItem[] };
  randomDelay: number;
  randomDelayMax: number | null;
  startImmediately: boolean;
  scheduledFor: string;
}

interface GroupCategoryGroup {
  id: string;
  groupId: string;
  groupName: string;
  sessionName: string;
}

interface GroupCategory {
  id: string;
  nome: string;
  cor?: string;
  descricao?: string | null;
  groups?: GroupCategoryGroup[];
}

export function GroupCampaignsPage() {
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [groups, setGroups] = useState<WAHAGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [groupCategories, setGroupCategories] = useState<GroupCategory[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    mode: 'existing' as 'existing' | 'new',
    categoryId: '',
    nome: '',
    cor: '#2563EB',
    descricao: ''
  });
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [categoryEditForm, setCategoryEditForm] = useState({
    id: '',
    nome: '',
    cor: '#2563EB',
    descricao: ''
  });
  const [savingCategoryEdit, setSavingCategoryEdit] = useState(false);
  const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);

  const [formData, setFormData] = useState<GroupCampaignForm>({
    nome: '',
    messageType: 'sequence',
    messageContent: { sequence: [] },
    randomDelay: 30,
    randomDelayMax: 80,
    startImmediately: true,
    scheduledFor: ''
  });
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
  const [fileInfos, setFileInfos] = useState<Record<string, { name: string; size: number; type: string }>>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers
    });
  };

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await authenticatedFetch('/api/waha/sessions');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      const workingSessions = data.filter((session: WhatsAppSession) => session.status === 'WORKING');
      setSessions(workingSessions);
    } catch (error) {
      console.error('Erro ao carregar sessões:', error);
      toast.error('Erro ao carregar sessões WhatsApp');
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadGroupCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await authenticatedFetch('/api/group-categories?includeGroups=true');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setGroupCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias de grupos:', error);
      toast.error('Erro ao carregar categorias de grupos');
    } finally {
      setLoadingCategories(false);
    }
  };


  useEffect(() => {
    loadSessions();
    loadGroupCategories();
  }, []);

  const loadGroups = async (sessionName: string) => {
    try {
      setLoadingGroups(true);
      const response = await authenticatedFetch(`/api/waha/sessions/${sessionName}/groups`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setGroups(data);
      toast.success(`${data.length} grupo(s) encontrados`);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
      toast.error('Erro ao carregar grupos da sessão');
    } finally {
      setLoadingGroups(false);
    }
  };

  const getGroupId = (group: WAHAGroup) => group.id || group.JID || group.LID || group.PhoneNumber || '';
  const getGroupName = (group: WAHAGroup) =>
    group.subject || group.Subject || group.name || group.Name || group.DisplayName || getGroupId(group) || 'Sem nome';
  const getGroupParticipants = (group: WAHAGroup) => group.participants || group.Participants || [];

  const handleSessionChange = (sessionName: string) => {
    setSelectedSession(sessionName);
    setGroups([]);
    setSelectedGroups([]);
    setSelectedCategoryFilter('all');
    if (sessionName) {
      loadGroups(sessionName);
    }
  };

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleSelectAllGroups = () => {
    if (selectedGroups.length === groups.length) {
      setSelectedGroups([]);
    } else {
      const ids = groups
        .map((group) => getGroupId(group))
        .filter((id): id is string => Boolean(id));
      setSelectedGroups(ids);
    }
  };

  const getSequence = () => formData.messageContent.sequence;

  const updateSequence = (updater: (current: SequenceItem[]) => SequenceItem[]) => {
    setFormData((prev) => ({
      ...prev,
      messageContent: {
        sequence: updater(prev.messageContent.sequence)
      }
    }));
  };

  const addMessageToSequence = () => {
    updateSequence((current) => [...current, { type: 'text', content: { text: '' } }]);
  };

  const removeSequenceItem = (index: number) => {
    updateSequence((current) => current.filter((_, i) => i !== index));
    setUploadingFiles((prev) => {
      const copy = { ...prev };
      delete copy[index.toString()];
      return copy;
    });
    setFileInfos((prev) => {
      const copy = { ...prev };
      delete copy[index.toString()];
      return copy;
    });
  };

  const getDefaultContentForType = (type: MessageType): SequenceItem['content'] => {
    switch (type) {
      case 'text':
        return { text: '', mentionEveryone: false };
      case 'image':
      case 'video':
        return { url: '', caption: '' };
      case 'audio':
        return { url: '' };
      case 'document':
        return { url: '', caption: '', fileName: '' };
      case 'openai':
      case 'groq':
        return { model: 'cx/gpt-6-luna', system: '', user: '' };
      case 'wait':
        return { waitTime: 30 };
      default:
        return { text: '' };
    }
  };

  const handleMessageTypeChange = (index: number, newType: MessageType) => {
    updateSequence((current) =>
      current.map((item, i) => (i === index ? { type: newType, content: getDefaultContentForType(newType) } : item))
    );
  };

  const handleTextChange = (index: number, text: string) => {
    updateSequence((current) =>
      current.map((item, i) => (i === index ? { ...item, content: { text } } : item))
    );
  };

  const handleAiFieldChange = (index: number, field: 'model' | 'system' | 'user', value: string) => {
    updateSequence((current) =>
      current.map((item, i) =>
        i === index
          ? {
              ...item,
              content: {
                ...item.content,
                [field]: value
              }
            }
          : item
      )
    );
  };

  const handleWaitChange = (index: number, waitTime: number) => {
    updateSequence((current) =>
      current.map((item, i) => (i === index ? { ...item, content: { waitTime: Math.max(1, waitTime) } } : item))
    );
  };

  const handleCaptionChange = (index: number, caption: string) => {
    updateSequence((current) =>
      current.map((item, i) =>
        i === index
          ? {
              ...item,
              content: {
                ...item.content,
                caption
              }
            }
          : item
      )
    );
  };

  const handleFileNameChange = (index: number, fileName: string) => {
    updateSequence((current) =>
      current.map((item, i) =>
        i === index
          ? {
              ...item,
              content: {
                ...item.content,
                fileName
              }
            }
          : item
      )
    );
  };

  const handleDragStart = (index: number) => setDraggedIndex(index);
  const handleDragEnd = () => setDraggedIndex(null);
  const handleDragOver = (event: DragEvent<HTMLDivElement>) => event.preventDefault();
  const handleDrop = (event: DragEvent<HTMLDivElement>, dropIndex: number) => {
    event.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    updateSequence((current) => {
      const clone = [...current];
      const [draggedItem] = clone.splice(draggedIndex, 1);
      clone.splice(dropIndex, 0, draggedItem);
      return clone;
    });
    setDraggedIndex(null);
  };

  const handleFileUpload = async (file: File, index: number) => {
    const key = index.toString();
    setUploadingFiles((prev) => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('auth_token');
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch('/api/media/upload', {
        method: 'POST',
        body: formData,
        headers
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Erro ao fazer upload');
      }
      const data = await response.json();
      setFileInfos((prev) => ({
        ...prev,
        [key]: {
          name: data.originalName,
          size: data.size,
          type: data.mimetype
        }
      }));
      updateSequence((current) =>
        current.map((item, i) =>
          i === index
            ? {
                ...item,
                content: {
                  ...item.content,
                  url: data.fileUrl,
                  fileName: data.originalName || item.content.fileName || ''
                }
              }
            : item
        )
      );
      toast.success('Arquivo carregado com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar arquivo');
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleMediaInputChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleFileUpload(file, index);
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    updateSequence((current) =>
      current.map((item, i) =>
        i === index
          ? {
              ...item,
              content: {
                ...item.content,
                url: '',
                caption: item.content.caption || ''
              }
            }
          : item
      )
    );
    setFileInfos((prev) => {
      const copy = { ...prev };
      delete copy[index.toString()];
      return copy;
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${units[i]}`;
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎬';
    if (type.startsWith('audio/')) return '🎵';
    return '📄';
  };

  const getCategoryGroupsForSession = (categoryId: string, sessionName: string) => {
    const category = groupCategories.find((cat) => cat.id === categoryId);
    if (!category || !category.groups) return [];
    return category.groups.filter((group) => group.sessionName === sessionName);
  };

  const filteredGroups = selectedCategoryFilter === 'all'
    ? groups
    : groups.filter((group) => {
        if (!selectedSession) return false;
        const groupId = getGroupId(group);
        if (!groupId) return false;
        const savedGroups = getCategoryGroupsForSession(selectedCategoryFilter, selectedSession);
        return savedGroups.some((saved) => saved.groupId === groupId);
      });

  const handleCreateCampaign = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedSession) {
      toast.error('Selecione uma sessão WhatsApp');
      return;
    }

    if (selectedGroups.length === 0) {
      toast.error('Selecione pelo menos um grupo');
      return;
    }

    const sequence = getSequence();
    if (sequence.length === 0) {
      toast.error('Adicione pelo menos uma mensagem à campanha');
      return;
    }

    try {
      const isSequence = sequence.length > 1;
      const finalMessageType = isSequence ? 'sequence' : sequence[0].type;
      const finalMessageContent = isSequence ? { sequence } : sequence[0].content;

      let scheduledForISO: string | null = null;
      if (!formData.startImmediately && formData.scheduledFor) {
        const localDate = new Date(formData.scheduledFor);
        scheduledForISO = localDate.toISOString();
      }

      const payload = {
        ...formData,
        messageType: finalMessageType,
        messageContent: finalMessageContent,
        sessionName: selectedSession,
        targetGroups: selectedGroups,
        scheduledFor: formData.startImmediately ? null : scheduledForISO
      };

      const response = await authenticatedFetch('/api/group-campaigns', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Campanha de grupo criada com sucesso!');
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao criar campanha');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      messageType: 'sequence',
      messageContent: { sequence: [] },
      randomDelay: 30,
      randomDelayMax: 80,
      startImmediately: true,
      scheduledFor: ''
    });
    setUploadingFiles({});
    setFileInfos({});
    setDraggedIndex(null);
    setSelectedGroups([]);
  };

  const openCategoryModal = () => {
    if (groupCategories.length === 0) {
      setCategoryForm({
        mode: 'new',
        categoryId: '',
        nome: '',
        cor: '#2563EB',
        descricao: ''
      });
    } else {
      setCategoryForm({
        mode: 'existing',
        categoryId: groupCategories[0]?.id || '',
        nome: '',
        cor: '#2563EB',
        descricao: ''
      });
    }
    setShowCategoryModal(true);
  };

  const openManageCategoriesModal = () => {
    if (groupCategories.length > 0) {
      startEditingCategory(groupCategories[0]);
    } else {
      setCategoryEditForm({ id: '', nome: '', cor: '#2563EB', descricao: '' });
    }
    setShowManageCategoriesModal(true);
  };

  const startEditingCategory = (category: GroupCategory) => {
    setCategoryEditForm({
      id: category.id,
      nome: category.nome,
      cor: category.cor || '#2563EB',
      descricao: category.descricao || ''
    });
  };

  const handleSelectCategoryForEdit = (categoryId: string) => {
    const selected = groupCategories.find((cat) => cat.id === categoryId);
    if (selected) {
      startEditingCategory(selected);
    } else {
      setCategoryEditForm({ id: '', nome: '', cor: '#2563EB', descricao: '' });
    }
  };

  const handleUpdateCategory = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!categoryEditForm.id) {
      toast.error('Selecione uma categoria para editar');
      return;
    }

    try {
      setSavingCategoryEdit(true);
      const response = await authenticatedFetch(`/api/group-categories/${categoryEditForm.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nome: categoryEditForm.nome,
          cor: categoryEditForm.cor,
          descricao: categoryEditForm.descricao || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Categoria atualizada com sucesso!');
      await loadGroupCategories();
      startEditingCategory({
        id: categoryEditForm.id,
        nome: categoryEditForm.nome,
        cor: categoryEditForm.cor,
        descricao: categoryEditForm.descricao,
        groups: []
      });
    } catch (error) {
      console.error('Erro ao atualizar categoria:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar categoria');
    } finally {
      setSavingCategoryEdit(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    const confirmed = window.confirm('Tem certeza que deseja excluir esta categoria?');
    if (!confirmed) {
      return;
    }

    try {
      setDeletingCategoryId(categoryId);
      const response = await authenticatedFetch(`/api/group-categories/${categoryId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      toast.success('Categoria excluída com sucesso!');
      if (selectedCategoryFilter === categoryId) {
        setSelectedCategoryFilter('all');
      }
      await loadGroupCategories();
      setCategoryEditForm({ id: '', nome: '', cor: '#2563EB', descricao: '' });
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir categoria');
    } finally {
      setDeletingCategoryId(null);
    }
  };

  const handleSaveGroupsToCategory = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedSession) {
      toast.error('Selecione uma sessão primeiro');
      return;
    }

    if (selectedGroups.length === 0) {
      toast.error('Selecione pelo menos um grupo');
      return;
    }

    try {
      setSavingCategory(true);

      let categoryId = categoryForm.categoryId;

      if (categoryForm.mode === 'new') {
        if (!categoryForm.nome.trim()) {
          toast.error('Informe um nome para a categoria');
          return;
        }

        const createResponse = await authenticatedFetch('/api/group-categories', {
          method: 'POST',
          body: JSON.stringify({
            nome: categoryForm.nome,
            cor: categoryForm.cor,
            descricao: categoryForm.descricao || undefined
          })
        });

        if (!createResponse.ok) {
          const errorData = await createResponse.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${createResponse.status}`);
        }

        const newCategory = await createResponse.json();
        categoryId = newCategory.id;
      }

      if (!categoryId) {
        toast.error('Selecione uma categoria');
        return;
      }

      const payloadGroups = selectedGroups
        .map((groupId) => {
          const group = groups.find((item) => getGroupId(item) === groupId);
          if (!group) return null;
          return {
            groupId,
            groupName: getGroupName(group),
            sessionName: selectedSession
          };
        })
        .filter((item): item is { groupId: string; groupName: string; sessionName: string } => Boolean(item));

      const assignResponse = await authenticatedFetch(`/api/group-categories/${categoryId}/groups`, {
        method: 'POST',
        body: JSON.stringify({ groups: payloadGroups })
      });

      if (!assignResponse.ok) {
        const errorData = await assignResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${assignResponse.status}`);
      }

      toast.success('Grupos salvos na categoria com sucesso!');
      setShowCategoryModal(false);
      loadGroupCategories();
    } catch (error) {
      console.error('Erro ao salvar grupos na categoria:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar grupos na categoria');
    } finally {
      setSavingCategory(false);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        title="Campanhas para Grupos (Apenas Whatsapp Não Oficial"
        subtitle="Envie campanhas com múltiplos tipos de mensagem para seus grupos"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Campanhas para Grupos</h1>
          <p className="mt-2 text-sm text-gray-600">
            Selecione uma sessão, escolha os grupos e crie sua mensagem.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">1. Selecione a Sessão WhatsApp</h2>
          {loadingSessions ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-4 text-gray-500">Nenhuma sessão ativa encontrada</div>
          ) : (
            <select
              value={selectedSession}
              onChange={(event) => handleSessionChange(event.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecione uma sessão...</option>
              {sessions.map((session) => (
                <option key={session.name} value={session.name}>
                  {session.displayName || session.name} ({session.provider === 'WAHA' ? 'WhatsApp Não Oficial' : session.provider})
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedSession && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">2. Selecione os Grupos</h2>
                {loadingCategories ? (
                  <p className="text-sm text-gray-500">Carregando categorias...</p>
                ) : groupCategories.length > 0 ? (
                  <p className="text-sm text-gray-500">Use uma categoria para filtrar grupos já salvos.</p>
                ) : (
                  <p className="text-sm text-gray-500">Crie categorias para reutilizar listas de grupos.</p>
                )}
              </div>
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700">Filtrar por categoria</label>
                  <select
                    value={selectedCategoryFilter}
                    onChange={(event) => setSelectedCategoryFilter(event.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Todos os grupos</option>
                    {groupCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={openManageCategoriesModal}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700"
                >
                  Gerenciar categorias
                </button>
                {groups.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAllGroups}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {selectedGroups.length === groups.length ? 'Desmarcar todos' : 'Selecionar todos'}
                  </button>
                )}
              </div>
            </div>
            {loadingGroups ? (
              <p className="mt-4 text-gray-600">Carregando grupos...</p>
            ) : filteredGroups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Nenhum grupo encontrado nesta sessão</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredGroups.map((group) => {
                  const groupId = getGroupId(group);
                  if (!groupId) return null;

                  const groupName = getGroupName(group);
                  const participantsCount = getGroupParticipants(group).length;

                  return (
                    <label
                      key={groupId}
                      className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedGroups.includes(groupId)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(groupId)}
                        onChange={() => handleGroupToggle(groupId)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-gray-900">{groupName}</div>
                        {participantsCount > 0 && (
                          <div className="text-sm text-gray-500">{participantsCount} participantes</div>
                        )}
                        <div className="text-xs text-gray-400 mt-1 truncate">{groupId}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {selectedGroups.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>{selectedGroups.length}</strong> grupo(s) selecionado(s)
                </p>
              </div>
            )}
          </div>
        )}

        {selectedSession && selectedGroups.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">3. Configurar Mensagem</h2>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-3 md:flex-row">
                <button
                  type="button"
                  onClick={openCategoryModal}
                  className="w-full md:w-1/3 px-6 py-3 border border-blue-200 text-blue-700 rounded-lg font-medium hover:bg-blue-50"
                  disabled={selectedGroups.length === 0}
                >
                  Salvar grupos em categoria
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="w-full md:w-1/3 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Criar Campanha para Grupos
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Salvar grupos em categoria</h3>
                <p className="text-sm text-gray-500">Selecione uma categoria existente ou crie uma nova</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCategoryModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveGroupsToCategory} className="px-6 py-6 space-y-5">
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="radio"
                    name="category-mode"
                    value="existing"
                    checked={categoryForm.mode === 'existing'}
                    onChange={() => setCategoryForm((prev) => ({ ...prev, mode: 'existing' }))}
                  />
                  Usar categoria existente
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="radio"
                    name="category-mode"
                    value="new"
                    checked={categoryForm.mode === 'new'}
                    onChange={() => setCategoryForm((prev) => ({ ...prev, mode: 'new' }))}
                  />
                  Criar nova categoria
                </label>
              </div>

              {categoryForm.mode === 'existing' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                  <select
                    value={categoryForm.categoryId}
                    onChange={(event) => setCategoryForm((prev) => ({ ...prev, categoryId: event.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecione...</option>
                    {groupCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.nome}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome da categoria</label>
                    <input
                      type="text"
                      value={categoryForm.nome}
                      onChange={(event) => setCategoryForm((prev) => ({ ...prev, nome: event.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Ex: Grupos VIP"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
                    <input
                      type="color"
                      value={categoryForm.cor}
                      onChange={(event) => setCategoryForm((prev) => ({ ...prev, cor: event.target.value }))}
                      className="h-10 w-16 cursor-pointer border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descrição (opcional)</label>
                    <textarea
                      value={categoryForm.descricao}
                      onChange={(event) => setCategoryForm((prev) => ({ ...prev, descricao: event.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                <p>
                  <strong>{selectedGroups.length}</strong> grupo(s) serão salvos na categoria selecionada.
                </p>
                {categoryForm.mode === 'existing' && selectedCategoryFilter !== 'all' && (
                  <p className="mt-1 text-blue-700">
                    Dica: use o filtro para conferir os grupos já salvos em cada categoria.
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-3 md:flex-row md:justify-end">
                <button
                  type="button"
                  onClick={() => setShowCategoryModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={savingCategory}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60"
                  disabled={savingCategory}
                >
                  {savingCategory ? 'Salvando...' : 'Salvar categoria'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showManageCategoriesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Gerenciar categorias de grupos</h3>
                <p className="text-sm text-gray-500">Edite ou exclua categorias existentes.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowManageCategoriesModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-6 space-y-6">
              {groupCategories.length === 0 ? (
                <div className="text-center text-sm text-gray-500">
                  Nenhuma categoria criada ainda. Crie uma categoria ao salvar grupos para começar.
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
                    <select
                      value={categoryEditForm.id}
                      onChange={(event) => handleSelectCategoryForEdit(event.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {groupCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  {categoryEditForm.id ? (
                    <form onSubmit={handleUpdateCategory} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                          <input
                            type="text"
                            value={categoryEditForm.nome}
                            onChange={(event) =>
                              setCategoryEditForm((prev) => ({
                                ...prev,
                                nome: event.target.value
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Cor</label>
                          <input
                            type="color"
                            value={categoryEditForm.cor}
                            onChange={(event) =>
                              setCategoryEditForm((prev) => ({
                                ...prev,
                                cor: event.target.value
                              }))
                            }
                            className="h-10 w-16 cursor-pointer border border-gray-300 rounded"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
                        <textarea
                          value={categoryEditForm.descricao}
                          onChange={(event) =>
                            setCategoryEditForm((prev) => ({
                              ...prev,
                              descricao: event.target.value
                            }))
                          }
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700">
                        <p>
                          Esta categoria possui{' '}
                          {groupCategories.find((cat) => cat.id === categoryEditForm.id)?.groups?.length || 0}{' '}
                          grupo(s) salvos.
                        </p>
                      </div>

                      <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
                        <button
                          type="button"
                          onClick={() => handleDeleteCategory(categoryEditForm.id)}
                          className="px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-60"
                          disabled={deletingCategoryId === categoryEditForm.id}
                        >
                          {deletingCategoryId === categoryEditForm.id ? 'Excluindo...' : 'Excluir categoria'}
                        </button>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setShowManageCategoriesModal(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                            disabled={savingCategoryEdit}
                          >
                            Fechar
                          </button>
                          <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60"
                            disabled={savingCategoryEdit}
                          >
                            {savingCategoryEdit ? 'Salvando...' : 'Salvar alterações'}
                          </button>
                        </div>
                      </div>
                    </form>
                  ) : (
                    <p className="text-sm text-gray-500">Selecione uma categoria para editar.</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Criar Campanha para Grupos</h3>
                <p className="text-sm text-gray-500">Monte a sequência de mensagens para os grupos selecionados</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateCampaign} className="px-6 py-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome da campanha</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(event) => setFormData({ ...formData, nome: event.target.value })}
                  placeholder="Ex: Campanha lançamento grupo VIP"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Sequência de mensagens</h4>
                    <p className="text-xs text-gray-500">Arraste para reordenar e personalize cada bloco</p>
                  </div>
                  <button
                    type="button"
                    onClick={addMessageToSequence}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                  >
                    <span className="text-lg leading-none">+</span>
                    Adicionar mensagem
                  </button>
                </div>

                {getSequence().length === 0 ? (
                  <div className="p-6 border-2 border-dashed border-gray-200 rounded-lg text-center text-sm text-gray-500">
                    Nenhuma mensagem adicionada ainda. Clique em "Adicionar mensagem" para começar.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {getSequence().map((item, index) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={handleDragOver}
                        onDrop={(event) => handleDrop(event, index)}
                        onDragEnd={handleDragEnd}
                        className={`border rounded-lg p-4 bg-white shadow-sm transition-all ${
                          draggedIndex === index ? 'border-blue-500 bg-blue-50 opacity-70' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <span className="cursor-move text-xl">⋮⋮</span>
                            <span className="font-medium text-gray-700">Mensagem {index + 1}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeSequenceItem(index)}
                            className="text-xs text-red-600 hover:text-red-700"
                          >
                            Remover
                          </button>
                        </div>

                        <div className="space-y-4">
                          <select
                            value={item.type}
                            onChange={(event) => handleMessageTypeChange(index, event.target.value as MessageType)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="text">💬 Texto</option>
                            <option value="image">🖼️ Imagem</option>
                            <option value="video">🎬 Vídeo</option>
                            <option value="audio">🎵 Áudio</option>
                            <option value="document">📄 Arquivo</option>
                            <option value="openai">🤖 OpenAI</option>
                            {/* Groq oculto: <option value="groq">⚡ Groq AI</option> */}
                            <option value="wait">⏳ Espera</option>
                          </select>

                          {item.type === 'text' && (
                            <div className="space-y-3">
                              <textarea
                                value={item.content.text || ''}
                                onChange={(event) => handleTextChange(index, event.target.value)}
                                rows={4}
                                placeholder="Digite sua mensagem"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={item.content.mentionEveryone || false}
                                  onChange={(event) => {
                                    updateSequence((current) =>
                                      current.map((msg, i) =>
                                        i === index
                                          ? {
                                              ...msg,
                                              content: {
                                                ...msg.content,
                                                mentionEveryone: event.target.checked
                                              }
                                            }
                                          : msg
                                      )
                                    );
                                  }}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span>@Mencionar todos no grupo</span>
                              </label>
                            </div>
                          )}

                          {['image', 'video', 'audio', 'document'].includes(item.type) && (
                            <div className="space-y-3">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <input
                                  type="file"
                                  accept={
                                    item.type === 'image'
                                      ? 'image/*'
                                      : item.type === 'video'
                                      ? 'video/*'
                                      : item.type === 'audio'
                                      ? 'audio/*'
                                      : '*'
                                  }
                                  onChange={(event) => handleMediaInputChange(event, index)}
                                  className="block text-sm text-gray-700"
                                />
                                {item.content.url && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveFile(index)}
                                    className="text-sm text-red-600 hover:text-red-700"
                                  >
                                    Remover arquivo
                                  </button>
                                )}
                              </div>

                              {uploadingFiles[index.toString()] && (
                                <p className="text-xs text-gray-500">Carregando arquivo...</p>
                              )}

                              {fileInfos[index.toString()] && (
                                <div className="flex items-center justify-between rounded bg-gray-50 p-3 text-sm text-gray-700">
                                  <span>
                                    {getFileIcon(fileInfos[index.toString()].type)} {fileInfos[index.toString()].name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatFileSize(fileInfos[index.toString()].size)}
                                  </span>
                                </div>
                              )}

                              {['image', 'video', 'document'].includes(item.type) && (
                                <input
                                  type="text"
                                  value={item.content.caption || ''}
                                  onChange={(event) => handleCaptionChange(index, event.target.value)}
                                  placeholder="Legenda (opcional)"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              )}

                              {item.type === 'document' && (
                                <input
                                  type="text"
                                  value={item.content.fileName || ''}
                                  onChange={(event) => handleFileNameChange(index, event.target.value)}
                                  placeholder="Nome do arquivo"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                              )}
                            </div>
                          )}

                          {item.type === 'openai' && (
                            <div className="space-y-3">
                              <p className="text-xs text-gray-500">Modelo: <strong>IntegraIA (cx/gpt-6-luna)</strong> — definido pelo sistema.</p>
                              <textarea
                                value={item.content.system || ''}
                                onChange={(event) => handleAiFieldChange(index, 'system', event.target.value)}
                                placeholder="Prompt do sistema"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <textarea
                                value={item.content.user || ''}
                                onChange={(event) => handleAiFieldChange(index, 'user', event.target.value)}
                                placeholder="Prompt do usuário"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          )}

                          {false && item.type === 'groq' && (
                            <div className="space-y-3">
                              <textarea
                                value={item.content.system || ''}
                                onChange={(event) => handleAiFieldChange(index, 'system', event.target.value)}
                                placeholder="Prompt do sistema"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              />
                              <textarea
                                value={item.content.user || ''}
                                onChange={(event) => handleAiFieldChange(index, 'user', event.target.value)}
                                placeholder="Prompt do usuário"
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                              />
                            </div>
                          )}

                          {item.type === 'wait' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Tempo de espera (segundos)</label>
                              <input
                                type="number"
                                min={1}
                                value={item.content.waitTime || 30}
                                onChange={(event) => handleWaitChange(index, Number(event.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Intervalo de Randomização (segundos)</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Mínimo</label>
                      <input
                        type="number"
                        min={0}
                        max={300}
                        value={formData.randomDelay}
                        onChange={(event) =>
                          setFormData({ ...formData, randomDelay: Number(event.target.value) || 0 })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <span className="text-gray-400 font-medium mt-5">a</span>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">Máximo</label>
                      <input
                        type="number"
                        min={0}
                        max={300}
                        value={formData.randomDelayMax ?? formData.randomDelay}
                        onChange={(event) =>
                          setFormData({ ...formData, randomDelayMax: Number(event.target.value) || 0 })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Intervalo aleatório entre envios ({formData.randomDelay}s - {formData.randomDelayMax ?? formData.randomDelay}s)</p>
                </div>

                <div>
                  <label className="flex items-center space-x-2 mb-3">
                    <input
                      type="checkbox"
                      checked={formData.startImmediately}
                      onChange={(event) =>
                        setFormData({ ...formData, startImmediately: event.target.checked })
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Iniciar imediatamente</span>
                  </label>

                  {!formData.startImmediately && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Agendar para</label>
                      <input
                        type="datetime-local"
                        value={formData.scheduledFor}
                        onChange={(event) => setFormData({ ...formData, scheduledFor: event.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700">
                <h4 className="font-semibold text-gray-900 mb-2">Resumo</h4>
                <ul className="space-y-1">
                  <li>
                    • Sessão: <strong>{sessions.find((session) => session.name === selectedSession)?.displayName || selectedSession}</strong>
                  </li>
                  <li>• Grupos selecionados: <strong>{selectedGroups.length}</strong></li>
                  <li>• Mensagens na sequência: <strong>{getSequence().length}</strong></li>
                  <li>• Delay: <strong>{formData.randomDelay}s - {formData.randomDelayMax ?? formData.randomDelay}s</strong></li>
                  <li>• Início: <strong>{formData.startImmediately ? 'Imediato' : 'Agendado'}</strong></li>
                </ul>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                >
                  Criar campanha
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}



